"use client";

import type { PostHog } from "posthog-js";
import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { PostApplyAlertReturn } from "@/components/apply/post-apply-alert-return";
import { PostApplyProLink } from "@/components/apply/post-apply-pro-link";
import { GoogleSignInButton } from "@/components/forms/google-sign-in-button";
import { MagicLinkForm } from "@/components/forms/magic-link-form";
import { TelegramChannelLink } from "@/components/telegram/telegram-channel-link";
import { Button } from "@/components/ui/button";
import { captureNudgeClient } from "@/lib/analytics/nudge-events";
import { trackJobInteraction } from "@/lib/analytics/track";
import {
	exposureFiredToday,
	markExposureFiredToday,
} from "@/lib/analytics/use-experiment";
import {
	claimConfirmForJob,
	isConfirmClaimedByOther,
	isConfirmSuppressedForJob,
	onConfirmClaimChange,
	onConfirmSuppressed,
	releaseConfirmForJob,
	suppressConfirmForJob,
} from "@/lib/apply/confirm-suppression";
import {
	daysSinceClick,
	outboundJobIdFromHref,
	PENDING_APPLY_TTL_MS,
	pickNextVisitCandidate,
	readPendingApplies,
	recordPendingApply,
	removePendingApply,
	updatePendingApply,
} from "@/lib/apply/pending-apply-store";
import { useSession } from "@/lib/auth/client";
import {
	deleteClientCookie,
	getClientCookie,
	setClientCookie,
} from "@/lib/cookies/client";
import {
	AHA_SIGNUP_ASK_FLAG,
	hasSeenAhaSignupAsk,
	resolveAhaSignupAskArm,
} from "@/lib/experiments/aha-signup-ask";
import {
	type AskArm,
	type AskTopic,
	buildPostApplyCallbackURL,
	forcedAskArm,
	isExcludedByAhaSignupAsk,
	POST_APPLY_ASK_FLAG,
	PREVIEW_FORCE_COOKIE,
	pickAskTopic,
	readJobCategory,
	readLastSearchQuery,
} from "@/lib/experiments/post-apply-single-ask";
import {
	decidePostApplyTrackPrompt,
	hasShownPostApplyTrackPrompt,
	markPostApplyTrackPromptShown,
	POST_APPLY_RETURN_SURFACE,
	POST_APPLY_TRACK_PROMPT_ANSWERED_EVENT,
	POST_APPLY_TRACK_PROMPT_FLAG,
	POST_APPLY_TRACK_PROMPT_SHOWN_EVENT,
	type PostApplyTrackPromptAnswer,
	type PostApplyTrackPromptArm,
	readPostApplyTrackPromptArm,
} from "@/lib/experiments/post-apply-track-prompt";
import { ANON_TTL_SECONDS } from "@/lib/hunt/anon-cookie";
import { buildJobApplyUrl } from "@/lib/sitemap/utils";
import enApplyConfirm from "@/locales/en/apply-confirm.json";
import idApplyConfirm from "@/locales/id/apply-confirm.json";

const COOKIE_NAME = "ld_pending_hop";
/** PostHog experiment flag for the "finish it" variant. The flag is the sole
 * switch: absent/off renders the plain toast; variant "test" (or boolean ON)
 * renders the finish-it card; any other variant is the holdout. */
const RETURN_FINISH_FLAG = "nudge-return-finish";

/** Don't ask "did you apply?" until the visitor has had time to actually do
 * it. Prod probe 2026-09-25: ~40% of toast prompts fired within 1 minute of
 * the outbound click, and answers were not_applied 77 vs applied 20. */
export const CONFIRM_MIN_DELAY_MS = 3 * 60 * 1000;
/** "Not yet" is a deferral, not a terminal answer: re-ask once after this. */
export const CONFIRM_SNOOZE_MS = 24 * 60 * 60 * 1000;
/** The snoozed cookie must outlive the snooze; the server's 2h TTL would not. */
const SNOOZE_COOKIE_MAX_AGE_SECONDS = 48 * 60 * 60;
/** Server flag that writes `ld_pending_hop` (and the guest hop row). The
 * next-visit ask runs only when it is on: either the cookie was seen for that
 * job, or PostHog reports the flag enabled. The flag stays the sole switch. */
const CONFIRM_LOOP_FLAG = "apply-confirm-loop-v2";
/** A guest's answer is recorded against the `hunt_anon_sid` cookie the hop
 * set (24h). Past that the confirm API 401s, so a guest is not asked. */
const GUEST_NEXT_VISIT_MAX_AGE_MS = ANON_TTL_SECONDS * 1000 - 5 * 60 * 1000;
/** While a next-visit prompt is on screen, look again this often for a
 * cookie re-ask that came due (it never replaces the visible prompt). */
const NEXT_VISIT_RECHECK_MS = 60 * 1000;

type PendingHop = {
	jobId: string;
	ts: number;
	firstApply?: boolean;
	/** P0-2 (telemetry-discovery-pmf-audit-2026-08-12, grill D5) — this apply
	 * completed past the daily-apply soft wall; render a Pro upsell line
	 * instead of the old hard `/pricing` block. */
	upsell?: boolean;
	/** Epoch ms. Set when the visitor answered "Not yet" once; the toast
	 * re-asks (once) after this time. Absent on cookies written by the server. */
	snoozedUntil?: number;
	/** Which path raised the prompt: the same-session tab return (cookie) or
	 * the next visit (localStorage pending-apply memory). */
	trigger?: "tab_return" | "next_visit";
	/** Next visit only: who the visitor applied to, when known. */
	company?: string | null;
	/** `post-apply-track-prompt` treatment: render the track-it card. */
	trackPrompt?: boolean;
};
type Answer = "applied" | "not_applied" | "blocked";

const SAVE_OFFER_SURFACE_ID = "confirm_apply_toast";
const SAVE_OFFER_TRIGGER = "confirm_not_yet";
type Status =
	| "idle"
	| "submitting"
	| "needs_signup"
	| "needs_email"
	| "offer_signup_save"
	| "first_apply_confirmed"
	| "reminded"
	/** Treatment card answered "applied": confirm and link the tracker. */
	| "tracked";

type Strings = {
	prompt: string;
	applied: string;
	notApplied: string;
	blocked: string;
	signupHeading: string;
	signupBody: string;
	signupCta: string;
	/** Guest answered "Not yet": offer to keep the job on a new account. */
	saveHeading: string;
	saveBody: string;
	saveGoogle: string;
	saveLater: string;
	trackHeading: string;
	trackBody: string;
	trackPlaceholder: string;
	trackCta: string;
	trackSkip: string;
	trackSaved: string;
	upsellNote: string;
	upsellCta: string;
	/** T10 delight pass — a distinct one-line confirmation for a visitor's
	 * FIRST tracked apply (not the account/email-capture prompts above,
	 * which already handle guests; this covers the plain "yes, applied"
	 * path an authed user or a repeat-later guest takes). */
	firstApplyConfirmed: string;
	/** "Finish it" variant (flag nudge-return-finish). Zeigarnik: an unfinished
	 * task nags; implementation intention: the user picks the time. */
	finishPrompt: (company: string) => string;
	finishContinue: string;
	finishApplied: string;
	finishRemind: string;
	finishReminded: string;
	/** Next-visit ask, worded by calendar days since the outbound click. */
	nextVisitPrompt: (days: number, company: string | null) => string;
	/** `post-apply-track-prompt` treatment card (signed-in tab return). */
	trackPromptHeading: (company: string | null) => string;
	trackPromptBody: string;
	trackPromptYes: string;
	trackPromptNotYet: string;
	trackPromptRemind: string;
	trackPromptDismiss: string;
	trackPromptTracked: string;
	trackPromptTrackerLink: string;
};

type NextVisitCopy = typeof idApplyConfirm;

function nextVisitPromptFrom(copy: NextVisitCopy) {
	return (days: number, company: string | null): string => {
		let template: string;
		if (days <= 0) {
			template = company ? copy.nextVisitToday : copy.nextVisitTodayNoCompany;
		} else if (days === 1) {
			template = company
				? copy.nextVisitYesterday
				: copy.nextVisitYesterdayNoCompany;
		} else {
			template = company
				? copy.nextVisitDaysAgo
				: copy.nextVisitDaysAgoNoCompany;
		}
		return template
			.replace("{company}", company ?? "")
			.replace("{days}", String(days));
	};
}

const EN: Strings = {
	prompt: "Did you apply to this job?",
	applied: "Yes, I applied",
	notApplied: "Not yet",
	blocked: "Couldn't apply",
	signupHeading: "Nice — save this to your pipeline?",
	signupBody:
		"Create a free account so this application isn't lost when your guest session ends.",
	signupCta: "Create account",
	saveHeading: "Keep this job for later?",
	saveBody:
		"Sign up free and this job goes on your application list, so you can find it again and finish when you're ready.",
	saveGoogle: "Sign up with Google",
	saveLater: "Maybe later",
	trackHeading: "Track this application?",
	trackBody:
		"Leave an email and we'll follow up on this one — no account needed.",
	trackPlaceholder: "you@example.com",
	trackCta: "Track it",
	trackSkip: "No thanks",
	trackSaved: "Got it — we'll follow up.",
	upsellNote: "You're a power applier today.",
	upsellCta: "See Pro",
	firstApplyConfirmed: "First application logged",
	finishPrompt: (company) =>
		`Your application to ${company} isn't finished? It usually takes only about 5 minutes.`,
	finishContinue: "Continue now",
	finishApplied: "Already applied",
	finishRemind: "Remind me tonight",
	finishReminded: "Done, we'll email you this evening.",
	nextVisitPrompt: nextVisitPromptFrom(enApplyConfirm),
	trackPromptHeading: (company) =>
		company ? `Did you apply to ${company}?` : "Did you apply to this job?",
	trackPromptBody:
		"Track it here so it lands on your applications list, ready for follow-up.",
	trackPromptYes: "Yes, I applied — track it",
	trackPromptNotYet: "Not yet",
	trackPromptRemind: "Remind me",
	trackPromptDismiss: "Dismiss",
	trackPromptTracked: "Tracked. It's on your applications list.",
	trackPromptTrackerLink: "See my applications",
};

const ID: Strings = {
	prompt: "Sudah melamar lowongan ini?",
	applied: "Sudah lamar",
	notApplied: "Belum, nanti saja",
	blocked: "Tidak bisa melamar",
	signupHeading: "Simpan lamaran ini ke pipeline kamu?",
	signupBody:
		"Buat akun gratis supaya lamaran ini tidak hilang saat sesi tamu berakhir.",
	signupCta: "Buat akun",
	saveHeading: "Simpan lowongan ini untuk nanti?",
	saveBody:
		"Daftar gratis, lowongan ini masuk ke daftar lamaranmu supaya gampang dicari lagi dan dilanjutkan kapan pun kamu siap.",
	saveGoogle: "Daftar dengan Google",
	saveLater: "Nanti saja",
	trackHeading: "Mau lamaran ini dilacak?",
	trackBody:
		"Kasih email, nanti kami kabari perkembangannya — tanpa perlu buat akun.",
	trackPlaceholder: "kamu@contoh.com",
	trackCta: "Lacak",
	trackSkip: "Tidak, terima kasih",
	trackSaved: "Oke — nanti kami kabari.",
	upsellNote: "Kamu pelamar aktif hari ini.",
	upsellCta: "Lihat Pro",
	firstApplyConfirmed: "Lamaran pertama tercatat",
	finishPrompt: (company) =>
		`Lamaranmu ke ${company} belum selesai? Biasanya cuma ±5 menit.`,
	finishContinue: "Lanjutkan sekarang",
	finishApplied: "Sudah lamar",
	finishRemind: "Ingatkan malam ini",
	finishReminded: "Oke, kami email kamu malam ini.",
	nextVisitPrompt: nextVisitPromptFrom(idApplyConfirm),
	trackPromptHeading: (company) =>
		company ? `Sudah lamar ke ${company}?` : "Sudah lamar lowongan ini?",
	trackPromptBody:
		"Catat di sini supaya masuk daftar lamaranmu dan gampang di-follow up.",
	trackPromptYes: "Ya, sudah lamar — catat",
	trackPromptNotYet: "Belum",
	trackPromptRemind: "Ingatkan saya",
	trackPromptDismiss: "Tutup",
	trackPromptTracked: "Tercatat. Sudah masuk daftar lamaranmu.",
	trackPromptTrackerLink: "Lihat lamaranku",
};

/**
 * `experiment_exposure` for `post-apply-track-prompt`, once per day per
 * browser, fired in BOTH arms at the same point (a signed-in seeker back
 * with a fresh pending hop). `$feature_flag_called` comes with the read.
 */
function exposeTrackPrompt(
	posthog: Pick<PostHog, "capture"> | null | undefined,
	arm: PostApplyTrackPromptArm,
	jobId: string,
) {
	if (exposureFiredToday(POST_APPLY_TRACK_PROMPT_FLAG)) return;
	markExposureFiredToday(POST_APPLY_TRACK_PROMPT_FLAG);
	posthog?.capture("experiment_exposure", {
		experiment: POST_APPLY_TRACK_PROMPT_FLAG,
		variant: arm,
		trigger: "tab_return",
		job_id: jobId,
	});
}

/** post-apply-single-ask test-arm card copy. `{topic}` is plain text. */
const ASK_COPY = {
	id: {
		headline: (topic: string | null) =>
			topic
				? `Mau loker ${topic} baru tiap pagi?`
				: "Mau loker baru seperti ini tiap pagi?",
		sent: "Cek email kamu — klik link-nya untuk mulai menerima loker tiap pagi.",
		answersLabel: "Sudah melamar?",
	},
	en: {
		headline: (topic: string | null) =>
			topic
				? `Want new ${topic} jobs every morning?`
				: "Want new jobs like this every morning?",
		sent: "Check your email — tap the link to start getting jobs every morning.",
		answersLabel: "Did you apply?",
	},
} as const;

/** MagicLinkForm strings for the card. The toast is layout-level and has no
 * page dictionary; these mirror src/locales/<locale>/common.json, except the
 * submit label, which names what the visitor gets. */
const ASK_FORM_DICT = {
	id: {
		emailAddress: "Alamat email",
		emailPlaceholder: "kamu@contoh.com",
		sendMagicLink: "Kirim ke email saya",
		sending: "Mengirim...",
		magicLinkError: "Gagal mengirim tautan masuk",
		unexpectedError: "Gagal — tidak ada yang hilang.",
		botCheckRequired: "Selesaikan dulu pengecekan bot sebelum lanjut.",
		botCheckFailed: "Pengecekan bot gagal. Coba refresh halaman, lalu ulangi.",
		botCheckLoading: "Memastikan kamu bukan robot…",
		botCheckUnavailable:
			"Pengecekan bot tidak bisa dimuat. Matikan ad blocker atau ekstensi yang memblokir challenges.cloudflare.com, lalu refresh halaman.",
		botCheckRetry: "Coba lagi",
	},
	en: {
		emailAddress: "Email address",
		emailPlaceholder: "you@example.com",
		sendMagicLink: "Email them to me",
		sending: "Sending...",
		magicLinkError: "Failed to send magic link",
		unexpectedError: "Couldn't complete that — nothing was lost.",
		botCheckRequired: "Please complete the bot check before continuing.",
		botCheckFailed: "Bot check failed. Please refresh the page and try again.",
		botCheckLoading: "Verifying you're human…",
		botCheckUnavailable:
			"Couldn't load the bot check. Disable any ad blocker or extensions blocking challenges.cloudflare.com, then refresh the page.",
		botCheckRetry: "Try again",
	},
} as const;

function readPendingHop(): PendingHop | null {
	if (typeof document === "undefined") return null;
	const raw = getClientCookie(COOKIE_NAME);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<PendingHop>;
		if (typeof parsed.jobId !== "string" || typeof parsed.ts !== "number") {
			return null;
		}
		return {
			jobId: parsed.jobId,
			ts: parsed.ts,
			firstApply: parsed.firstApply === true,
			upsell: parsed.upsell === true,
			...(typeof parsed.snoozedUntil === "number"
				? { snoozedUntil: parsed.snoozedUntil }
				: {}),
		};
	} catch {
		return null;
	}
}

/** Public `{ title, company }` of one job (edge-cached route). Never rejects:
 * any failure resolves to nulls and the caller falls back to generic copy. */
async function fetchJobSummary(
	jobId: string,
): Promise<{ title: string | null; company: string | null }> {
	const empty = { title: null, company: null };
	try {
		const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/summary`);
		if (!res.ok) return empty;
		const body: unknown = await res.json();
		if (!body || typeof body !== "object") return empty;
		const { title, company } = body as Record<string, unknown>;
		return {
			title: typeof title === "string" && title ? title : null,
			company: typeof company === "string" && company ? company : null,
		};
	} catch {
		return empty;
	}
}

function clearPendingHop() {
	deleteClientCookie(COOKIE_NAME);
}

/** Ms until this hop may be shown; <= 0 means show now. A snoozed hop waits
 * for its snooze; a fresh hop waits until the visitor has had time to apply. */
function msUntilEligible(hop: PendingHop, now: number): number {
	if (typeof hop.snoozedUntil === "number") return hop.snoozedUntil - now;
	return hop.ts + CONFIRM_MIN_DELAY_MS - now;
}

/** "Not yet" once -> snooze; "Not yet" again (already snoozed) -> clear. */
function settleAfterAnswer(hop: PendingHop, value: Answer) {
	// A next-visit ask is not the cookie's job (the cookie, if any, belongs to
	// a different hop): answering it only settles the pending-apply memory,
	// and a "Not yet" there is final — it was already the second chance.
	if (hop.trigger === "next_visit") {
		removePendingApply(hop.jobId);
		return;
	}
	if (value === "not_applied" && typeof hop.snoozedUntil !== "number") {
		const {
			trigger: _trigger,
			company: _company,
			trackPrompt: _trackPrompt,
			...cookieHop
		} = hop;
		setClientCookie(
			COOKIE_NAME,
			JSON.stringify({
				...cookieHop,
				snoozedUntil: Date.now() + CONFIRM_SNOOZE_MS,
			}),
			{ maxAgeSeconds: SNOOZE_COOKIE_MAX_AGE_SECONDS },
		);
		return;
	}
	clearPendingHop();
}

export type ConfirmApplyToastProps = { locale?: string };

/** Id of the one mounted toast instance that may render; see `ConfirmApplyToast`. */
let activeToastInstance: string | null = null;

/** Exported for tests: the singleton outlives a test. */
export function resetConfirmApplyToastSingleton(): void {
	activeToastInstance = null;
}

/**
 * Exactly one toast per page. It is mounted once in the locale layout today,
 * but a second mount (a nested layout, a zone shell) would arm a second copy
 * off the same cookie and double both the prompt and its impression — the
 * same denominator-doubling the in-page ask already had to dedupe. The first
 * mounted instance owns the slot; any other renders nothing and runs none of
 * the cookie/impression effects.
 */
export function ConfirmApplyToast(props: ConfirmApplyToastProps) {
	const instanceId = useId();
	const [isOwner, setIsOwner] = useState(false);
	useEffect(() => {
		if (activeToastInstance !== null && activeToastInstance !== instanceId) {
			return;
		}
		activeToastInstance = instanceId;
		setIsOwner(true);
		return () => {
			if (activeToastInstance === instanceId) activeToastInstance = null;
		};
	}, [instanceId]);
	return isOwner ? (
		<>
			<ConfirmApplyToastInner {...props} />
			{/* post-apply-single-ask magic-link return trip; renders nothing
			    unless the URL carries ?alert_capture=post_apply. */}
			<PostApplyAlertReturn locale={props.locale} />
		</>
	) : null;
}

/**
 * Real-time counterpart to the email-based apply-confirm cadence — shows a
 * lightweight prompt when the visitor returns to this tab after an outbound
 * `/out/[jobId]` hop (P0-1, product-turnaround-execution-2026-08-03).
 *
 * Gated implicitly on the `apply-confirm-loop-v2` flag: the `ld_pending_hop`
 * cookie this component reads is only ever set by the `/out/[jobId]` route
 * when that flag is on, so no separate client-side flag check is needed
 * here — no cookie means the flag was off (or there's genuinely nothing
 * pending), and the component renders nothing either way.
 */
function ConfirmApplyToastInner({ locale }: ConfirmApplyToastProps) {
	const t = locale === "en" ? EN : ID;
	const posthog = usePostHog();
	const [hop, setHop] = useState<PendingHop | null>(null);
	// Identifies THIS surface in the cross-surface suppression signal so it
	// never suppresses itself mid-answer.
	const surfaceId = useId();
	const [status, setStatus] = useState<Status>("idle");
	const [email, setEmail] = useState("");
	const [emailSaved, setEmailSaved] = useState(false);

	// "Finish it" variant. Variant "test"/true = nudge; any other string =
	// holdout (counted, not nudged); undefined/false = flag off, plain toast.
	const returnFinishVariant = useFeatureFlagVariantKey(RETURN_FINISH_FLAG);
	const finishActive =
		returnFinishVariant === "test" || returnFinishVariant === true;
	const finishHoldout =
		typeof returnFinishVariant === "string" && returnFinishVariant !== "test";
	const { data: session, isPending: sessionPending } = useSession();
	const signedIn = Boolean(session?.user);

	// post-apply-single-ask. The flag is the sole switch: `null` until PostHog
	// answers (and when the flag is absent) = no experiment, today's prompt.
	// true = test arm, false = control arm (today's prompt, counted).
	const [askFlag, setAskFlag] = useState<boolean | null>(null);
	// Mutual exclusion with `aha-signup-ask` (PR #7600): its arm is read with
	// send_event:false so this toast never records an exposure for it.
	const [ahaArm, setAhaArm] = useState<unknown>(undefined);
	useEffect(() => {
		if (!posthog) return;
		const check = () => {
			const v = posthog.getFeatureFlag?.(POST_APPLY_ASK_FLAG);
			setAskFlag(v === true ? true : v === false ? false : null);
			setAhaArm(
				posthog.getFeatureFlag?.(AHA_SIGNUP_ASK_FLAG, {
					send_event: false,
				}),
			);
		};
		check();
		return posthog.onFeatureFlags?.(check);
	}, [posthog]);
	// E2E only: lanes never initialise client PostHog, so the arm comes from
	// the `ld_preview_force` cookie — honoured on localhost / *.workers.dev
	// only, never on lokerdollar.com (see `isForceableHost`).
	const [forcedArm, setForcedArm] = useState<AskArm | null>(null);
	useEffect(() => {
		try {
			setForcedArm(
				forcedAskArm(
					getClientCookie(PREVIEW_FORCE_COOKIE),
					window.location.hostname,
				),
			);
		} catch {
			// No cookie access — no force.
		}
	}, []);
	const askArm: AskArm | null = isExcludedByAhaSignupAsk({
		ahaArm: resolveAhaSignupAskArm(ahaArm),
		sawAhaCard: hasSeenAhaSignupAsk(),
	})
		? null
		: (forcedArm ??
			(askFlag === true ? "test" : askFlag === false ? "control" : null));
	const [summary, setSummary] = useState<{
		jobId: string;
		title: string;
		company: string;
	} | null>(null);

	const recheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Next-visit state. `checkPending` is a stable callback, so everything it
	// reads that changes across renders goes through a ref.
	const pageLoadAt = useRef(Date.now());
	const currentHopRef = useRef<PendingHop | null>(null);
	currentHopRef.current = hop;
	const statusRef = useRef<Status>(status);
	statusRef.current = status;
	const signedInRef = useRef(signedIn);
	signedInRef.current = signedIn;
	const posthogRef = useRef(posthog);
	posthogRef.current = posthog;
	/** One next-visit prompt per page view: a second pending job waits for the
	 * next page load instead of chaining prompts. */
	const nextVisitShown = useRef(false);
	const summaryTried = useRef(new Map<string, "loading" | "done">());

	const showHop = useCallback((next: PendingHop | null) => {
		currentHopRef.current = next;
		setHop(next);
	}, []);

	const checkPending = useCallback(() => {
		if (recheckTimer.current) {
			clearTimeout(recheckTimer.current);
			recheckTimer.current = null;
		}
		// The track-it card (post-apply-track-prompt treatment) stays until the
		// visitor acts on it: a later focus/tab return must not swap it for the
		// plain toast, and its own "tracked" beat must not vanish either.
		if (currentHopRef.current?.trackPrompt) return;
		// A next-visit prompt on screen is never swapped out from under the
		// visitor; a cookie re-ask that came due meanwhile waits its turn.
		if (currentHopRef.current?.trigger === "next_visit") {
			recheckTimer.current = setTimeout(checkPending, NEXT_VISIT_RECHECK_MS);
			return;
		}
		const pending = readPendingHop();
		// The cookie exists only when the server flag wrote it on a real hop:
		// that is what makes this job's pending-apply memory askable later.
		if (pending) updatePendingApply(pending.jobId, { hopSeen: true });

		// 1. Same-session tab-return path (cookie) — unchanged behaviour.
		// No cookie: nothing pending, or the visitor already answered (answering
		// clears it) — a stale in-memory hop must not flash back in.
		// A sibling surface (the in-feed followup banner) may already have
		// asked about this job and got an answer; the cookie stays until this
		// toast answers, so without this check the visitor is asked twice.
		// The in-page ask holds this job's slot (it armed on the apply click,
		// flag `apply-inpage-confirm-v1`): one prompt per job, so stay hidden.
		// Re-checked when that claim is released (see the effect below).
		if (
			pending &&
			!isConfirmSuppressedForJob(pending.jobId) &&
			!isConfirmClaimedByOther(pending.jobId, "toast")
		) {
			// `post-apply-track-prompt`: a signed-in seeker in the treatment arm
			// is asked on the first return after POST_APPLY_TRACK_MIN_AWAY_MS
			// instead of after CONFIRM_MIN_DELAY_MS. Every other case (guest,
			// control, unresolved flag, snoozed re-ask, already asked about
			// this job) falls through to the unchanged path below.
			const track = decidePostApplyTrackPrompt({
				signedIn: signedInRef.current,
				hop: pending,
				now: Date.now(),
				alreadyShown: hasShownPostApplyTrackPrompt(pending.jobId),
				readArm: () => {
					const arm = readPostApplyTrackPromptArm(posthogRef.current);
					if (arm) exposeTrackPrompt(posthogRef.current, arm, pending.jobId);
					return arm;
				},
			});
			if (track.kind === "wait") {
				recheckTimer.current = setTimeout(checkPending, track.ms + 50);
				showHop(null);
				return;
			}
			if (track.kind === "show" && claimConfirmForJob(pending.jobId, "toast")) {
				showHop({ ...pending, trigger: "tab_return", trackPrompt: true });
				return;
			}
			const wait = msUntilEligible(pending, Date.now());
			if (wait > 0) {
				// Too early (or snoozed): stay hidden and look again when eligible,
				// instead of showing on the first visibilitychange.
				recheckTimer.current = setTimeout(checkPending, wait + 50);
				// A fresh hop owns the slot until it is asked; a snoozed one waits
				// a day, so the next-visit ask below may use the slot meanwhile.
				if (typeof pending.snoozedUntil !== "number") {
					showHop(null);
					return;
				}
			} else if (claimConfirmForJob(pending.jobId, "toast")) {
				showHop({ ...pending, trigger: "tab_return" });
				return;
			}
		}

		// 2. Next visit: a pending apply from an EARLIER page view that the
		// tab-return path never got to ask (cookie expired, tab closed).
		// Never while a post-answer panel (signup / email capture) is open.
		if (nextVisitShown.current || statusRef.current !== "idle") {
			showHop(null);
			return;
		}
		const now = Date.now();
		const flagOn = posthogRef.current?.isFeatureEnabled?.(CONFIRM_LOOP_FLAG);
		const candidate = pickNextVisitCandidate(readPendingApplies(now), {
			now,
			pageLoadAt: pageLoadAt.current,
			minDelayMs: CONFIRM_MIN_DELAY_MS,
			maxAgeMs: signedInRef.current
				? PENDING_APPLY_TTL_MS
				: GUEST_NEXT_VISIT_MAX_AGE_MS,
			skip: (e) =>
				// The cookie path owns its job (asking or snoozed).
				e.jobId === pending?.jobId ||
				(!e.hopSeen && flagOn !== true) ||
				isConfirmSuppressedForJob(e.jobId) ||
				isConfirmClaimedByOther(e.jobId, "toast"),
		});
		if (!candidate) {
			showHop(null);
			return;
		}
		// The anchor listener knows only the job id; fetch the company once
		// (edge-cached public read), then look again. A failure falls back to
		// the generic wording rather than skipping the ask.
		const summaryState = summaryTried.current.get(candidate.jobId);
		if (!candidate.company && summaryState === "loading") {
			showHop(null);
			return;
		}
		if (!candidate.company && summaryState === undefined) {
			summaryTried.current.set(candidate.jobId, "loading");
			showHop(null);
			fetchJobSummary(candidate.jobId)
				.then((j) => {
					if (j.company || j.title) {
						updatePendingApply(candidate.jobId, j);
					}
				})
				.finally(() => {
					summaryTried.current.set(candidate.jobId, "done");
					checkPending();
				});
			return;
		}
		if (!claimConfirmForJob(candidate.jobId, "toast")) {
			showHop(null);
			return;
		}
		nextVisitShown.current = true;
		showHop({
			jobId: candidate.jobId,
			ts: candidate.clickedAt,
			trigger: "next_visit",
			company: candidate.company,
		});
	}, [showHop]);

	useEffect(() => {
		checkPending();
		const onVisibility = () => {
			if (document.visibilityState === "visible") checkPending();
		};
		// Some browsers return to a tab with a window focus and no
		// visibilitychange (a separate window, not a tab). Same check.
		const onFocus = () => checkPending();
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("focus", onFocus);
		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("focus", onFocus);
			if (recheckTimer.current) clearTimeout(recheckTimer.current);
		};
	}, [checkPending]);

	// Pending-apply memory: every outbound `/<locale>/out/<id>` link click on
	// any surface (job page CTA, feed rows, Hunt cards) is remembered for 7
	// days, so a visitor who never comes back to THIS tab is still asked on
	// their next visit. Capture phase on `document` runs before the link's own
	// React handler, which may enrich the entry with company/title.
	useEffect(() => {
		const peekTimers: ReturnType<typeof setTimeout>[] = [];
		const onClick = (event: MouseEvent) => {
			if (event.type === "auxclick" && event.button !== 1) return;
			const target = event.target;
			if (!(target instanceof Element)) return;
			const anchor = target.closest("a[href]");
			if (!(anchor instanceof HTMLAnchorElement)) return;
			const jobId = outboundJobIdFromHref(
				anchor.getAttribute("href") ?? "",
				window.location.href,
			);
			if (!jobId) return;
			recordPendingApply({ jobId });
			// The hop opens in a new tab and sets `ld_pending_hop` there; peek
			// for it so this job is marked as a real, flag-on hop even if the
			// visitor never returns to this tab.
			for (const delay of [2_000, 8_000]) {
				peekTimers.push(
					setTimeout(() => {
						const seen = readPendingHop();
						if (seen?.jobId === jobId) {
							updatePendingApply(jobId, { hopSeen: true });
						}
					}, delay),
				);
			}
		};
		document.addEventListener("click", onClick, true);
		document.addEventListener("auxclick", onClick, true);
		return () => {
			document.removeEventListener("click", onClick, true);
			document.removeEventListener("auxclick", onClick, true);
			for (const t of peekTimers) clearTimeout(t);
		};
	}, []);

	// Session and flags resolve after mount; a guest-vs-member window or a
	// flag that just arrived can make a next-visit ask eligible. Only while
	// nothing is on screen.
	useEffect(() => {
		signedInRef.current = signedIn;
		if (!currentHopRef.current) checkPending();
	}, [signedIn, checkPending]);
	useEffect(() => {
		const unsubscribe = posthog?.onFeatureFlags?.(() => {
			if (!currentHopRef.current) checkPending();
		});
		return () => {
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, [posthog, checkPending]);

	// Hold the job's slot only while this toast is on screen, so the in-page
	// ask stays hidden meanwhile and can take over once we are gone.
	const claimedJobId = hop?.jobId ?? null;
	useEffect(() => {
		if (!claimedJobId) return;
		return () => releaseConfirmForJob(claimedJobId, "toast");
	}, [claimedJobId]);

	// The in-page ask released its claim (its arm expired, or it was answered):
	// look again — `checkPending` re-applies every other gate. Only while
	// hidden: a toast already on screen (possibly mid-answer, e.g. the signup
	// step after its cookie was cleared) must not be re-evaluated from under
	// the visitor.
	const showingRef = useRef(false);
	showingRef.current = hop !== null;
	useEffect(
		() =>
			onConfirmClaimChange(() => {
				if (!showingRef.current) checkPending();
			}),
		[checkPending],
	);

	// A sibling surface answered this job while this toast was already on
	// screen. Storage is checked on mount and tab-return, neither of which
	// happens here — without this the visitor answers the banner and the toast
	// keeps asking the same question.
	useEffect(() => {
		if (!hop) return;
		return onConfirmSuppressed(
			hop.jobId,
			() => {
				setHop(null);
				setStatus("idle");
			},
			surfaceId,
		);
	}, [hop, surfaceId]);

	// Impression. Without it this prompt is the funnel's one genuinely dark
	// instrument: `apply_confirm_answered` has fired twice in its entire life,
	// and with no denominator PostHog cannot separate "the toast never
	// rendered" from "it rendered and nobody tapped" — two problems with
	// completely different fixes. WCA leg (a) counts only rows this prompt
	// promotes, so an unmeasurable prompt is an unexplainable north star.
	//
	// Deduped per jobId via a ref, NOT per mount: `checkPending` re-runs on
	// every `visibilitychange`, which is exactly the tab-return this toast
	// exists to catch, so a mount-scoped guard would re-fire on each return.
	const impressionFiredFor = useRef<string | null>(null);
	useEffect(() => {
		if (!hop || status !== "idle") return;
		if (impressionFiredFor.current === hop.jobId) return;
		impressionFiredFor.current = hop.jobId;
		const msSinceHop = Math.max(0, Date.now() - hop.ts);
		const trigger = hop.trigger ?? "tab_return";
		// Shown once per pending click: the memory keeps the entry (so a quick
		// re-click is recognised) but never asks about it again.
		if (trigger === "next_visit") {
			updatePendingApply(hop.jobId, { askedAt: Date.now() });
		}
		posthog?.capture("apply_confirm_prompt_shown", {
			job_id: hop.jobId,
			// NOT "this person's first ever apply". The server sets it from
			// `isGuestFirstApply(readGuestApplyCap().used)`, whose counter is
			// keyed `guest-apply:<ip-hash>:<yyyymmdd>` — so it means "first guest
			// apply from this IP HASH, TODAY (UTC)". A shared NAT, a returning
			// visitor on day 2 and a genuine first-timer are indistinguishable
			// here, and `false` does NOT imply the person has applied before.
			//
			// Stated at the emission site because reading it as per-person has
			// already produced a wrong conclusion once (2026-09-01 guest-email
			// capture analysis, docs/plans/backlog/deferred-ledger.md).
			first_apply: hop.firstApply === true,
			locale: locale === "en" ? "en" : "id",
			// How long after the outbound hop the visitor came back. A prompt
			// shown 3 seconds after the click is a different signal from one
			// shown 20 minutes later, and only one of them is worth trusting.
			ms_since_hop: msSinceHop,
			// True on the one re-ask after a "Not yet" snooze.
			re_ask: typeof hop.snoozedUntil === "number",
			// "tab_return" = same-session cookie path; "next_visit" = the
			// 7-day pending-apply memory on a later page view.
			trigger,
			ms_since_click: msSinceHop,
			track_prompt: hop.trackPrompt === true,
		});
		if (hop.trackPrompt) {
			// Once per job: answering, dismissing or leaving all count.
			markPostApplyTrackPromptShown(hop.jobId);
			posthog?.capture(POST_APPLY_TRACK_PROMPT_SHOWN_EVENT, {
				job_id: hop.jobId,
				ms_since_hop: msSinceHop,
				locale: locale === "en" ? "en" : "id",
			});
		}
		// Server-side twin of the capture above. The PostHog event is the lossy
		// side (consent, ad-block, and — as of 2026-09-03 — an API key with no
		// read scopes, so it cannot be queried at all), and this prompt is the
		// only denominator WCA's in-app leg will ever have. Same fire-and-forget
		// beacon the apply click already uses, so it fails closed and silent.
		trackJobInteraction({
			jobId: hop.jobId,
			interactionType: "confirm_prompt_shown",
			surface: "confirm_apply_toast",
			locale: locale === "en" ? "en" : "id",
			metadata: {
				first_apply: hop.firstApply === true,
				upsell: hop.upsell === true,
				ms_since_hop: msSinceHop,
				trigger,
				ms_since_click: msSinceHop,
			},
		});
	}, [hop, status, posthog, locale]);

	// The cookie carries only a job id; the finish-it copy needs the company.
	// Failure leaves `summary` null, which falls back to the plain prompt.
	// The finish-it card is a same-session nudge; the next-visit ask has its
	// own wording, so it never fetches for or renders that variant.
	const hopJobId = hop && hop.trigger !== "next_visit" ? hop.jobId : null;
	// The track-it card names the company too, when the summary arrives.
	const wantsSummary = finishActive || hop?.trackPrompt === true;
	useEffect(() => {
		if (!hopJobId || !wantsSummary) return;
		let cancelled = false;
		fetchJobSummary(hopJobId).then(({ title, company }) => {
			if (cancelled || !title || !company) return;
			setSummary({ jobId: hopJobId, title, company });
		});
		return () => {
			cancelled = true;
		};
	}, [hopJobId, wantsSummary]);

	const finishView =
		finishActive &&
		hop &&
		hop.trigger !== "next_visit" &&
		summary?.jobId === hop.jobId
			? summary
			: null;

	// post-apply-single-ask: a guest's same-session tab-return toast, with the
	// experiment resolved to an arm. Never for signed-in users, never for the
	// next-visit ask, and never while the nudge-return-finish test arm owns the
	// card (so neither experiment contaminates the other).
	const askJobId =
		hop &&
		hop.trigger === "tab_return" &&
		!signedIn &&
		!sessionPending &&
		!finishActive &&
		askArm !== null
			? hop.jobId
			: null;
	const askCurrent = useMemo<{
		jobId: string;
		arm: AskArm;
		topic: AskTopic;
	} | null>(
		() =>
			askJobId && askArm
				? {
						jobId: askJobId,
						arm: askArm,
						topic: pickAskTopic({
							searchQuery: readLastSearchQuery(),
							category: readJobCategory(askJobId),
						}),
					}
				: null,
		[askJobId, askArm],
	);
	// The arm and topic a hop was counted under stay fixed for that hop.
	const [askLocked, setAskLocked] = useState<typeof askCurrent>(null);
	const ask =
		hop && askLocked?.jobId === hop.jobId && hop.trigger === "tab_return"
			? askLocked
			: askCurrent;
	const askProps = useMemo(
		() =>
			ask
				? {
						variant: ask.arm,
						topic_source: ask.topic.source,
						job_id: ask.jobId,
						locale: locale === "en" ? "en" : "id",
					}
				: null,
		[ask, locale],
	);
	const askFiredFor = useRef<string | null>(null);
	useEffect(() => {
		if (!askCurrent || status !== "idle") return;
		if (askFiredFor.current === askCurrent.jobId) return;
		askFiredFor.current = askCurrent.jobId;
		setAskLocked(askCurrent);
		const props = {
			variant: askCurrent.arm,
			topic_source: askCurrent.topic.source,
			job_id: askCurrent.jobId,
			locale: locale === "en" ? "en" : "id",
		};
		// Denominator, both arms: the control arm still sees today's prompt.
		posthog?.capture("post_apply_ask_eligible", props);
		if (askCurrent.arm === "test") {
			posthog?.capture("post_apply_ask_shown", props);
		}
	}, [askCurrent, status, posthog, locale]);
	const [askSentFor, setAskSentFor] = useState<string | null>(null);

	// Nudge telemetry (shared contract). Once per job per mount, like the
	// impression above.
	const nudgeFiredFor = useRef<string | null>(null);
	useEffect(() => {
		if (!hop || status !== "idle") return;
		// The track-it card replaces the finish card; it is not that nudge.
		if (hop.trackPrompt) return;
		if (!finishView && !finishHoldout) return;
		if (nudgeFiredFor.current === hop.jobId) return;
		nudgeFiredFor.current = hop.jobId;
		if (finishView) {
			captureNudgeClient(posthog, {
				event: "nudge_shown",
				nudgeId: "return_finish",
				channel: "in_app",
				variant: "test",
				jobId: hop.jobId,
			});
		} else {
			captureNudgeClient(posthog, {
				event: "nudge_suppressed",
				nudgeId: "return_finish",
				channel: "in_app",
				reason: "holdout",
				jobId: hop.jobId,
			});
		}
	}, [hop, status, finishView, finishHoldout, posthog]);

	const continueNow = useCallback(() => {
		if (!hop || !finishView) return;
		captureNudgeClient(posthog, {
			event: "nudge_clicked",
			nudgeId: "return_finish",
			channel: "in_app",
			jobId: hop.jobId,
		});
		posthog?.capture("nudge_return_finish_clicked", { job_id: hop.jobId });
		window.open(
			buildJobApplyUrl({
				jobId: hop.jobId,
				title: finishView.title,
				company: finishView.company,
				locale: locale === "en" ? "en" : "id",
			}),
			"_blank",
			"noopener",
		);
	}, [hop, finishView, posthog, locale]);

	const captureTrackAnswer = useCallback(
		(answer: PostApplyTrackPromptAnswer) => {
			if (!hop?.trackPrompt) return;
			posthog?.capture(POST_APPLY_TRACK_PROMPT_ANSWERED_EVENT, {
				job_id: hop.jobId,
				answer,
			});
		},
		[hop, posthog],
	);

	const remindMe = useCallback(async () => {
		if (!hop || status === "submitting") return;
		captureTrackAnswer("remind");
		// Asked to be reminded: terminal for the in-app asks about this job.
		if (hop.trackPrompt) suppressConfirmForJob(hop.jobId, surfaceId);
		setStatus("submitting");
		try {
			await fetch("/api/apply/remind", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jobId: hop.jobId }),
			});
		} catch {
			// Best-effort; the daily outcome emails remain the fallback.
		}
		clearPendingHop();
		setStatus("reminded");
	}, [hop, status, captureTrackAnswer, surfaceId]);

	// Track-it card close button: not an answer, so no confirm row; but the
	// card is shown once per job, so the in-app asks stop for this job too.
	const dismissTrackPrompt = useCallback(() => {
		if (!hop) return;
		captureTrackAnswer("dismiss");
		suppressConfirmForJob(hop.jobId, surfaceId);
		removePendingApply(hop.jobId);
		clearPendingHop();
		setHop(null);
		setStatus("idle");
	}, [hop, captureTrackAnswer, surfaceId]);

	// Auto-dismiss the first-apply confirmation — it's a beat, not a modal
	// that needs a close tap.
	useEffect(() => {
		if (
			status !== "first_apply_confirmed" &&
			status !== "reminded" &&
			status !== "tracked"
		) {
			return;
		}
		// "tracked" carries a link to the tracker; give it time to be used.
		const timer = setTimeout(
			() => {
				setHop(null);
				setStatus("idle");
			},
			status === "tracked" ? 8000 : 2200,
		);
		return () => clearTimeout(timer);
	}, [status]);

	const answer = useCallback(
		async (value: Answer) => {
			if (!hop || status === "submitting") return;
			captureTrackAnswer(value === "applied" ? "applied" : "not_yet");
			setStatus("submitting");
			const jobId = hop.jobId;
			// Silence the sibling surfaces first: this is a terminal answer for
			// this job whatever the network does next.
			suppressConfirmForJob(jobId, surfaceId);
			try {
				const res = await fetch("/api/apply/confirm", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						jobId,
						answer: value,
						...(hop.trackPrompt ? { surface: POST_APPLY_RETURN_SURFACE } : {}),
					}),
				});
				const data = (await res.json().catch(() => null)) as {
					requiresSignupToKeep?: boolean;
					offerSignupToSave?: boolean;
				} | null;
				settleAfterAnswer(hop, value);
				if (data?.requiresSignupToKeep) {
					// T3 — a guest on their FIRST tracked apply (server-set
					// `hop.firstApply`, itself gated on `guest-first-apply-v1`) gets
					// the lightweight email-only capture instead of the full
					// account-creation nudge; every other guest apply keeps today's
					// signup nudge unchanged.
					setStatus(hop.firstApply ? "needs_email" : "needs_signup");
				} else if (data?.offerSignupToSave) {
					// Guest "Not yet": the server only sets this for guests. A
					// dismissable sign-up ask, never a wall.
					setStatus("offer_signup_save");
				} else if (value === "applied" && hop.trackPrompt && res.ok) {
					setStatus("tracked");
				} else if (value === "applied" && hop.firstApply) {
					// T10 delight pass — a first tracked apply that DIDN'T need
					// signup/email capture (authed user, or a repeat guest who
					// already has one on file) still deserves a distinct beat
					// instead of the toast silently vanishing. Auto-dismisses below.
					setStatus("first_apply_confirmed");
				} else {
					setHop(null);
					setStatus("idle");
				}
			} catch {
				// Best-effort — dismiss either way. Authed users still get the
				// day-2/day-6 email nudge (`outcome-followup-enqueuer.ts`) as a
				// fallback if this fetch never lands.
				settleAfterAnswer(hop, value);
				setHop(null);
				setStatus("idle");
			}
		},
		[hop, status, surfaceId, captureTrackAnswer],
	);

	// One `gate_impression` per toast instance for the "Not yet" sign-up ask,
	// same event shape as `GuestSaveSheet` so the sign-up gate funnels read it.
	const saveOfferShown = useRef(false);
	useEffect(() => {
		if (status !== "offer_signup_save" || saveOfferShown.current) return;
		saveOfferShown.current = true;
		posthog?.capture("gate_impression", {
			surface_id: SAVE_OFFER_SURFACE_ID,
			gate_type: "signup",
			trigger: SAVE_OFFER_TRIGGER,
			mode: "soft",
			job_id: hop?.jobId,
		});
	}, [status, posthog, hop]);

	const dismissSaveOffer = useCallback(() => {
		posthog?.capture("gate_cta_clicked", {
			surface_id: SAVE_OFFER_SURFACE_ID,
			gate_type: "signup",
			cta_label: "later",
			trigger: SAVE_OFFER_TRIGGER,
		});
		setHop(null);
		setStatus("idle");
	}, [posthog]);

	const submitEmail = useCallback(async () => {
		if (!hop || emailSaved) return;
		const jobId = hop.jobId;
		try {
			await fetch("/api/apply/track-email", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jobId, email }),
			});
		} catch {
			// Best-effort — this is a nice-to-have nudge, not a required step.
		}
		setEmailSaved(true);
	}, [hop, email, emailSaved]);

	if (!hop) return null;

	const base =
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50";

	// Answer buttons: >=44px tap target (WCAG 2.5.5 / Apple HIG).
	const tap = "min-h-11";

	// This toast is z-50 — above the page-owned mobile Apply CTA (z-40, see
	// job-apply-cta.tsx), which stays mounted the whole time (external applies
	// open in a new tab), so the toast must stack ABOVE it, never on it.
	// The fixed `bottom-20` this replaced was a guess; the bar's real height
	// (button + risk line + safe-area, and the ad bar stacked above it) varies,
	// and on mobile job detail the toast covered the footer's own content.
	// The bar publishes its rendered height as --sticky-cta-h and the ad bar
	// as --ad-bar-h (0px / unset when absent or `lg:hidden`), so stack on
	// those. Desktop (lg+, where the bar is hidden) keeps `bottom-4`.
	const bottomOffset =
		"bottom-[calc(var(--sticky-cta-h,0px)+var(--ad-bar-h,0px)+1rem)] lg:bottom-4";

	// apply-cap-wall test arm (server sets `upsell` on the hop). Shared by the
	// plain prompt AND the nudge-return-finish card: on 2026-10-27 the owner's
	// test-arm hop carried upsell:true but the finish card never rendered it,
	// so the two experiments together hid the offer from every user in both
	// test arms.
	const upsellLine = hop.upsell ? (
		<p className="text-xs text-muted-foreground">
			{t.upsellNote}{" "}
			<a
				href={`/${locale === "en" ? "en" : "id"}/pricing`}
				className={`font-medium text-primary underline-offset-4 hover:underline ${base}`}
			>
				{t.upsellCta}
			</a>
		</p>
	) : null;

	// post-apply-single-ask test arm, on the plain-prompt branch only (every
	// other status / variant branch below wins first, unchanged).
	const askCard = ask && ask.arm === "test" && askProps ? ask : null;
	const askLocale = locale === "en" ? "en" : "id";
	const askCopy = ASK_COPY[askLocale];
	const askCardActive =
		askCard !== null &&
		finishView === null &&
		(status === "idle" || status === "submitting");
	// Capped so the card never covers a 360px-wide phone; scrolls inside.
	const askHeightCap = askCardActive ? " max-h-[60vh] overflow-y-auto" : "";

	return (
		<output
			data-testid="confirm-apply-toast"
			data-status={status}
			className={`fixed inset-x-4 ${bottomOffset} z-50 mx-auto block max-w-sm rounded-xl ${hop.trackPrompt && status === "idle" ? "border-2 border-primary" : "border border-border"} bg-card p-4 text-card-foreground shadow-lg sm:left-auto sm:right-4${askHeightCap}`}
		>
			{status === "needs_email" ? (
				<div className="flex flex-col gap-2">
					{emailSaved ? (
						<p className="text-sm font-medium">{t.trackSaved}</p>
					) : (
						<>
							<p className="text-sm font-medium">{t.trackHeading}</p>
							<p className="text-sm text-muted-foreground">{t.trackBody}</p>
							<form
								className="flex flex-col gap-2"
								onSubmit={(e) => {
									e.preventDefault();
									submitEmail();
								}}
							>
								<label htmlFor="confirm-toast-email" className="sr-only">
									{t.trackPlaceholder}
								</label>
								<input
									id="confirm-toast-email"
									type="email"
									inputMode="email"
									autoComplete="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder={t.trackPlaceholder}
									className={`h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground ${base}`}
								/>
								<div className="flex gap-2">
									<button
										type="submit"
										disabled={!email.trim()}
										className={`flex-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground ${base}`}
									>
										{t.trackCta}
									</button>
									<button
										type="button"
										onClick={() => {
											setHop(null);
											setStatus("idle");
										}}
										className={`rounded-lg border border-border px-3 py-1.5 text-sm text-foreground ${base}`}
									>
										{t.trackSkip}
									</button>
								</div>
							</form>
						</>
					)}
				</div>
			) : status === "needs_signup" ? (
				<div className="flex flex-col gap-2">
					<p className="text-sm font-medium">{t.signupHeading}</p>
					<p className="text-sm text-muted-foreground">{t.signupBody}</p>
					<a
						href={`/${locale === "en" ? "en" : "id"}/signup?callbackURL=%2F${locale === "en" ? "en" : "id"}%2Fseeker`}
						className={`inline-flex items-center justify-center rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground ${base}`}
					>
						{t.signupCta}
					</a>
				</div>
			) : status === "offer_signup_save" ? (
				<div className="flex flex-col gap-2" data-testid="confirm-save-offer">
					<p className="text-sm font-medium">{t.saveHeading}</p>
					<p className="text-sm text-muted-foreground">{t.saveBody}</p>
					<div
						onClickCapture={() => {
							posthog?.capture("gate_cta_clicked", {
								surface_id: SAVE_OFFER_SURFACE_ID,
								gate_type: "signup",
								cta_label: "google",
								trigger: SAVE_OFFER_TRIGGER,
							});
						}}
					>
						<GoogleSignInButton
							callbackURL={`${window.location.pathname}${window.location.search}`}
							label={t.saveGoogle}
							context="signup"
						/>
					</div>
					<button
						type="button"
						onClick={dismissSaveOffer}
						className={`${tap} rounded-lg border border-border px-3 py-1.5 text-sm text-foreground ${base}`}
					>
						{t.saveLater}
					</button>
				</div>
			) : status === "first_apply_confirmed" ? (
				<p className="text-sm font-medium text-foreground">
					{t.firstApplyConfirmed}
				</p>
			) : status === "reminded" ? (
				<p className="text-sm font-medium text-foreground">
					{t.finishReminded}
				</p>
			) : status === "tracked" ? (
				<div className="flex flex-col gap-2" data-testid="post-apply-tracked">
					<p className="text-sm font-medium text-foreground">
						{t.trackPromptTracked}
					</p>
					<a
						href={`/${locale === "en" ? "en" : "id"}/applications`}
						className={`text-sm font-medium text-primary underline-offset-4 hover:underline ${base}`}
					>
						{t.trackPromptTrackerLink}
					</a>
					<PostApplyProLink locale={locale === "en" ? "en" : "id"} />
					<TelegramChannelLink
						surface="post_apply"
						locale={locale === "en" ? "en" : "id"}
					/>
				</div>
			) : hop.trackPrompt ? (
				<div
					className="flex flex-col gap-3"
					data-testid="post-apply-track-prompt"
				>
					<div className="flex items-start justify-between gap-2">
						<p className="text-base font-semibold text-foreground">
							{t.trackPromptHeading(
								summary?.jobId === hop.jobId ? summary.company : null,
							)}
						</p>
						<button
							type="button"
							onClick={dismissTrackPrompt}
							aria-label={t.trackPromptDismiss}
							className={`-mr-1 -mt-1 inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-lg leading-none text-muted-foreground hover:bg-muted ${base}`}
						>
							<span aria-hidden="true">×</span>
						</button>
					</div>
					{upsellLine}
					<p className="text-sm text-muted-foreground">{t.trackPromptBody}</p>
					<div className="flex flex-col gap-2">
						<button
							type="button"
							onClick={() => answer("applied")}
							disabled={status === "submitting"}
							className={`${tap} rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground ${base}`}
						>
							{t.trackPromptYes}
						</button>
						<div className="flex gap-2">
							<button
								type="button"
								onClick={() => answer("not_applied")}
								disabled={status === "submitting"}
								className={`${tap} flex-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground ${base}`}
							>
								{t.trackPromptNotYet}
							</button>
							<button
								type="button"
								onClick={remindMe}
								disabled={status === "submitting"}
								className={`${tap} flex-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground ${base}`}
							>
								{t.trackPromptRemind}
							</button>
						</div>
					</div>
				</div>
			) : finishView ? (
				<div className="flex flex-col gap-3" data-testid="nudge-return-finish">
					{upsellLine}
					<p className="text-sm font-medium">
						{t.finishPrompt(finishView.company)}
					</p>
					<div className="flex flex-wrap gap-2">
						<Button
							type="button"
							size="xs"
							className={tap}
							onClick={continueNow}
						>
							{t.finishContinue}
						</Button>
						<button
							type="button"
							onClick={() => answer("applied")}
							disabled={status === "submitting"}
							className={`${tap} rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground ${base}`}
						>
							{t.finishApplied}
						</button>
						{signedIn ? (
							<button
								type="button"
								onClick={remindMe}
								disabled={status === "submitting"}
								className={`${tap} rounded-lg px-3 py-1.5 text-sm text-muted-foreground ${base}`}
							>
								{t.finishRemind}
							</button>
						) : (
							<button
								type="button"
								onClick={() => answer("not_applied")}
								disabled={status === "submitting"}
								className={`${tap} rounded-lg px-3 py-1.5 text-sm text-muted-foreground ${base}`}
							>
								{t.notApplied}
							</button>
						)}
					</div>
				</div>
			) : askCardActive && askCard && askProps ? (
				<section
					className="flex flex-col gap-3"
					aria-labelledby="post-apply-ask-title"
					data-testid="post-apply-ask"
					data-topic-source={askCard.topic.source}
				>
					{upsellLine}
					<p id="post-apply-ask-title" className="text-sm font-semibold">
						{askCopy.headline(askCard.topic.label)}
					</p>
					{askSentFor === askCard.jobId ? (
						<p
							role="status"
							className="text-sm text-muted-foreground"
							data-testid="post-apply-ask-sent"
						>
							{askCopy.sent}
						</p>
					) : (
						<MagicLinkForm
							locale={askLocale}
							t={ASK_FORM_DICT[askLocale]}
							emailInputId="post-apply-ask-email"
							callbackURL={buildPostApplyCallbackURL(
								window.location.href,
								askCard.topic.filters,
							)}
							onSent={() => {
								posthog?.capture("post_apply_ask_submitted", askProps);
								setAskSentFor(askCard.jobId);
							}}
						/>
					)}
					<div
						className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border pt-2"
						data-testid="post-apply-ask-answers"
					>
						<span className="text-xs text-muted-foreground">
							{askCopy.answersLabel}
						</span>
						{(["applied", "not_applied"] as const).map((value) => (
							<button
								key={value}
								type="button"
								onClick={() => {
									posthog?.capture("post_apply_ask_answered", {
										...askProps,
										answer: value,
									});
									answer(value);
								}}
								disabled={status === "submitting"}
								className={`${tap} rounded-lg px-2 py-1 text-xs font-medium text-foreground underline-offset-4 hover:underline ${base}`}
							>
								{value === "applied" ? t.applied : t.notApplied}
							</button>
						))}
					</div>
				</section>
			) : (
				<div className="flex flex-col gap-3">
					{upsellLine}
					<p className="text-sm font-medium" data-trigger={hop.trigger}>
						{hop.trigger === "next_visit"
							? t.nextVisitPrompt(
									daysSinceClick(hop.ts, Date.now()),
									hop.company ?? null,
								)
							: t.prompt}
					</p>
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							onClick={() => answer("applied")}
							disabled={status === "submitting"}
							className={`${tap} rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground ${base}`}
						>
							{t.applied}
						</button>
						<button
							type="button"
							onClick={() => answer("not_applied")}
							disabled={status === "submitting"}
							className={`${tap} rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground ${base}`}
						>
							{t.notApplied}
						</button>
						<button
							type="button"
							onClick={() => answer("blocked")}
							disabled={status === "submitting"}
							className={`${tap} rounded-lg px-3 py-1.5 text-sm text-muted-foreground ${base}`}
						>
							{t.blocked}
						</button>
					</div>
				</div>
			)}
		</output>
	);
}
